import React, { createContext, useState, useContext, ReactNode } from 'react';
import { Toast } from 'flowbite-react';
import {
  HiCheck,
  HiX,
  HiExclamation,
  HiInformationCircle,
} from 'react-icons/hi';
import classNames from 'classnames';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = (message: string, type: ToastType) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);

    // Auto remove toast after 5 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 5000);
  };

  const getToastIcon = (type: ToastType) => {
    const basicClasses =
      'h-8 w-8 items-center justify-center rounded-lg bg-green-100 text-green-500';
    switch (type) {
      case 'success':
        return (
          <HiCheck
            className={classNames(basicClasses, 'bg-green-100 text-green-500')}
          />
        );
      case 'error':
        return (
          <HiX
            className={classNames(basicClasses, 'bg-red-100 text-red-500')}
          />
        );
      case 'warning':
        return (
          <HiExclamation
            className={classNames(
              basicClasses,
              'bg-warning text-warning-light',
            )}
          />
        );
      case 'info':
        return (
          <HiInformationCircle
            className={classNames(
              basicClasses,
              'bg-yellow-100 text-yellow-500',
            )}
          />
        );
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast container - fixed at the top right */}
      <div className="fixed top-[88px] right-3 z-50 flex flex-col gap-2 w-auto max-w-xs text-red-normal">
        {toasts.map((toast) => (
          <Toast key={toast.id} className="w-full">
            <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
              {getToastIcon(toast.type)}
            </div>
            <div className="ml-3 mr-2 text-sm font-normal">{toast.message}</div>
            <Toast.Toggle
              onDismiss={() =>
                setToasts((prev) => prev.filter((t) => t.id !== toast.id))
              }
            />
          </Toast>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
